// frontend/src/components/Samples/MyTasksEditModal.jsx
//
// Standalone modal for editing a single line operation (task).
// Previously inline in MyTasksList — extracted so it can be reused
// from MyTasksList, a calendar view, a dashboard widget, etc.
//
// Props:
//   task      – row object from lineOperationsAPI.getMyTasks / getAll
//   onSave    – async (publicId, formData) => void   (caller handles API + toast)
//   onCancel  – () => void

// frontend/src/components/Samples/MyTasksEditModal.jsx
//
// Standalone modal for editing a single line operation (task).
//
// Props:
//   task      – row object from lineOperationsAPI.getMyTasks / getAll
//   onSave    – async (publicId, formData) => void
//               formData.apply_to = { public_ids: [...], fields: {...} } when the user
//               ticked other lines to receive the same data (caller saves them too)
//   onCancel  – () => void

import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import '../Settings/Modal.css';
import './MyTasksEditModal.css';
import FileUpload from '../Common/FileUpload';
import { attachmentsAPI, lineOperationsAPI } from '../../api/client';
import { FiPaperclip, FiClock, FiCopy } from 'react-icons/fi';
import AuditLogPanel from '../Common/AuditLog/AuditLogPanel';
import { TASK_STATUS_META, statusPillStyle } from '../../constants/statusMeta';
import { useTranslation } from '../../context/TranslationContext';

const todayStr = () => new Date().toISOString().slice(0, 10);


// Mirror of getOperationUI in LineOperationDetailsModal
const parseOptions = (raw) => {
  if (!raw) return null;
  if (Array.isArray(raw)) return raw.length ? raw : null;
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) && parsed.length ? parsed : null;
  } catch { return null; }
};

const getOperationUI = (task) => {
  const options = parseOptions(task.field_options || task.bool_field_label);
  if (options?.length) return { kind: 'choice', label: task.bool_field_label || 'Resultado', options };
  if (task.text_field_label) return { kind: 'text', label: task.text_field_label };
  return { kind: 'notes_only', label: 'Observação' };
};


// ── Shared helpers (mirrored from LineOperationDetailsModal) ──────────────────

const PasteTextarea = ({ value, onChange, onImagePaste, placeholder, rows = 2 }) => {
  const ref = useRef(null);
  const handlePaste = useCallback((e) => {
    const items = Array.from(e.clipboardData?.items || []);
    const imgItem = items.find(i => i.type.startsWith('image/'));
    if (!imgItem) return;
    e.preventDefault();
    const file = imgItem.getAsFile();
    if (file && onImagePaste) onImagePaste(file);
  }, [onImagePaste]);
  return (
    <textarea
      ref={ref}
      className="field-input"
      value={value}
      onChange={e => onChange(e.target.value)}
      onPaste={handlePaste}
      placeholder={placeholder}
      rows={rows}
    />
  );
};

const ImageThumbs = ({ files, onRemove }) => {
  if (!files?.length) return null;
  return (
    <div className="pending-images">
      {files.map((file, i) => (
        <div key={i} className="pending-img-wrap">
          <img src={URL.createObjectURL(file)} alt="" className="pending-img" />
          <button type="button" className="pending-img-remove" onClick={() => onRemove(i)}>×</button>
        </div>
      ))}
    </div>
  );
};

const MyTasksEditModal = ({ task, onSave, onCancel }) => {
  const { t } = useTranslation();
  const ui = useMemo(() => getOperationUI(task), [task]);

  const [form, setForm] = useState({
    status:             task.status              || 'NEW',
    planned_start_date: task.planned_start_date?.slice(0, 10) || '',
    planned_end_date:   task.planned_end_date  ?.slice(0, 10) || '',
    real_start_date:    task.real_start_date   ?.slice(0, 10) || '',
    real_end_date:      task.real_end_date     ?.slice(0, 10) || '',
    operation_instructions: task.operation_instructions || '',
    completion_notes:       task.completion_notes       || '',
    // result_value comes back as result_value from DB; map to ui-specific key
    choice_value:       ui.kind === 'choice' ? (task.result_value || '') : '',
    text_value:         ui.kind === 'text'   ? (task.result_value || '') : '',
    pendingImages:      [],
  });
  const [saving, setSaving] = useState(false);
  const [error,  setError]  = useState('');
  const [showAuditLog, setShowAuditLog] = useState(false);

  // ── "Apply to other lines" (same order + same operation) ──
  const [siblings,   setSiblings]   = useState([]);
  const [selected,   setSelected]   = useState(() => new Set());   // public_ids
  const [applyOpen,  setApplyOpen]  = useState(true);
  const [applyFields, setApplyFields] = useState({
    status: true, real_dates: true, result: true,
    operation_instructions: true, completion_notes: true, attachments: true,
  });

  useEffect(() => {
    let alive = true;
    if (!task.public_id) return undefined;
    lineOperationsAPI.getSiblings(task.public_id)
      .then(r => { if (alive) setSiblings(r.data?.data || []); })
      .catch(() => { /* optional feature — silently ignore */ });
    return () => { alive = false; };
  }, [task.public_id]);

  const toggleSibling = (id) => setSelected(prev => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const allSelected = siblings.length > 0 && selected.size === siblings.length;
  const toggleAll = () =>
    setSelected(allSelected ? new Set() : new Set(siblings.map(s => s.public_id)));
  const toggleField = (k) => setApplyFields(prev => ({ ...prev, [k]: !prev[k] }));

  const addImage = (file) => setForm(prev => ({
    ...prev, pendingImages: [...(prev.pendingImages || []), file]
  }));

  const removeImage = (i) => setForm(prev => ({
    ...prev, pendingImages: (prev.pendingImages || []).filter((_, idx) => idx !== i)
  }));

  const setF = (k, v) => setForm(prev => {
    const next = { ...prev, [k]: v };
    if (k === 'status') {
      if (v === 'COMPLETED' && !next.real_end_date) {
        next.real_end_date = todayStr();
      } else if (v !== 'COMPLETED' && next.real_end_date) {
        next.real_end_date = '';
      }
    }
    return next;
  });

  useEffect(() => {
    document.body.classList.add('modal-open');
    const onKey = e => { if (e.key === 'Escape') onCancel(); };
    document.addEventListener('keydown', onKey);
    return () => {
      document.body.classList.remove('modal-open');
      document.removeEventListener('keydown', onKey);
    };
  }, [onCancel]);

  const handleSubmit = async e => {
    e.preventDefault();
    if (form.status === 'COMPLETED' && !form.real_end_date) {
      setError('Data de conclusão real é obrigatória para marcar como Concluído.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const resultValue = form.choice_value || form.text_value || null;

      // Only the fields ticked in "Campos a copiar" travel to the other lines.
      const targets = siblings.filter(s => selected.has(s.public_id));
      const applyTo = targets.length ? {
        public_ids: targets.map(s => s.public_id),
        fields: {
          ...(applyFields.status && { status: form.status }),
          ...(applyFields.real_dates && {
            real_start_date: form.real_start_date,
            real_end_date:   form.real_end_date,
          }),
          ...(applyFields.result && { result_value: resultValue }),
          ...(applyFields.operation_instructions && { operation_instructions: form.operation_instructions }),
          ...(applyFields.completion_notes && { completion_notes: form.completion_notes }),
        },
      } : null;

      await onSave(task.public_id, {
        ...form,
        result_value: resultValue,
        ...(applyTo && { apply_to: applyTo }),
      });
      if (task.line_operations_id && form.pendingImages?.length) {
        try {
          await attachmentsAPI.upload('line_operation', task.line_operations_id, form.pendingImages);
        } catch (e) { console.error('attachment upload failed:', e); }
      }
      // Same new attachments on the other selected lines
      if (applyTo && applyFields.attachments && form.pendingImages?.length) {
        for (const s of targets) {
          try {
            await attachmentsAPI.upload('line_operation', s.line_operations_id, form.pendingImages);
          } catch (e) { console.error('attachment upload (sibling) failed:', e); }
        }
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Erro ao guardar');
      setSaving(false);
    }
  };

  // Date input — adds data-empty when blank to suppress browser's mm/dd/yyyy mask
  const DateInput = ({ fieldKey }) => (
    <input
      type="date"
      className="field-input"
      value={form[fieldKey]}
      {...(!form[fieldKey] ? { 'data-empty': true } : {})}
      onChange={e => setF(fieldKey, e.target.value)}
    />
  );

  return (
    <div className="modal-overlay">
      <div className="modal-content" style={{ maxWidth: 680 }}>

        <div className="modal-header">
          <div>
            <h3>{task.operation_label}</h3>
            <div className="mtem-meta-row">
              {task.order_public_id ? (
                <a href={`/samples/orders/${task.order_public_id}`}
                   target="_blank" rel="noopener noreferrer"
                   className="mtem-pill mtem-pill--order"
                   title="Abrir ordem de amostra">
                  {task.sample_order_no} ↗
                </a>
              ) : task.sample_order_no ? (
                <span className="mtem-pill mtem-pill--order">{task.sample_order_no}</span>
              ) : null}
              {task.matrix_line_no != null && (
                <span className="mtem-pill mtem-pill--line">L{task.matrix_line_no}</span>
              )}
              {task.area_code && (
                <span className="mtem-pill mtem-pill--area">{task.area_code}</span>
              )}
              {task.entered_product_code && (
                <span className="mtem-pill mtem-pill--product">
                  {task.entered_product_code}
                  {task.entered_product_color && (
                    <span style={{ marginLeft: 5, fontWeight: 400, color: 'var(--gray-400)' }}>
                      · {task.entered_product_color}
                    </span>
                  )}
                </span>
              )}
              {task.erp_product_code && task.erp_product_code !== task.entered_product_code && (
                <span className="mtem-pill mtem-pill--erp">
                  ERP: {task.erp_product_code}
                  {task.erp_product_color_code && ` / ${task.erp_product_color_code}`}
                </span>
              )}
              {task.customer_name && (
                <span className="mtem-pill mtem-pill--customer">{task.customer_name}</span>
              )}
              {task.responsible_name && (
                <span className="mtem-pill mtem-pill--user">👤 {task.responsible_name}</span>
              )}
            </div>
          </div>
          {task.line_operations_id && (
            <button
              type="button"
              className="modal-close"
              title="Ver histórico de alterações"
              onClick={() => setShowAuditLog(true)}
              style={{ marginRight: 4 }}
            >
              <FiClock size={16} />
            </button>
          )}
          <button className="modal-close" type="button" onClick={onCancel}>×</button>
        </div>

        <form onSubmit={handleSubmit} className="modal-form field-stack">
          {error && <div className="form-error">⚠️ {error}</div>}

          {/* Status pills */}
          <div className="field-row">
            <label className="field-label">Estado</label>
            <div className="status-pill-group">
              {Object.entries(TASK_STATUS_META).map(([s, m]) => {
                const active = form.status === s;
                return (
                  <button key={s} type="button" className="status-pill"
                    onClick={() => setF('status', s)}
                    style={{ border: '1.5px solid', ...statusPillStyle(m, active) }}>
                    {m.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Planned dates */}
          <div className="field-row">
            <div className="task-modal-section task-modal-section--planned">
              <div className="task-modal-date-grid">
                {[
                  ['planned_start_date', 'Início Planeado'],
                  ['planned_end_date',   'Fim Planeado'],
                ].map(([k, lbl]) => (
                  <div key={k} className="field-row">
                    <label className="field-label">{lbl}</label>
                    <DateInput fieldKey={k} />
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Real dates */}
          <div className="field-row">
            <div className="task-modal-section task-modal-section--real">
              <div className="task-modal-date-grid">
                {[
                  ['real_start_date', 'Início Real'],
                  ['real_end_date',   'Fim Real'],
                ].map(([k, lbl]) => (
                  <div key={k} className="field-row">
                    <label className="field-label">{lbl}</label>
                    <div className="task-modal-date-row">
                      <DateInput fieldKey={k} />
                      {!form[k] && (
                        <button
                          type="button"
                          className="btn-today"
                          title="Hoje"
                          onClick={() => setF(k, todayStr())}
                        >
                          Hoje
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Result — choice or text, only when operation has a result field */}
          {ui.kind === 'choice' && (
            <div className="field-row">
              <label className="field-label">{ui.label} <span className="field-hint">(obrigatório)</span></label>
              <div className="pill-group">
                {ui.options.map(opt => (
                  <button key={opt} type="button"
                    className={`pill ${form.choice_value === opt ? 'selected' : ''}`}
                    onClick={() => setF('choice_value', form.choice_value === opt ? '' : opt)}>
                    {opt}
                  </button>
                ))}
                {form.choice_value && (
                  <button type="button" className="pill-clear"
                    onClick={() => setF('choice_value', '')}>
                    limpar
                  </button>
                )}
              </div>
            </div>
          )}

          {ui.kind === 'text' && (
            <div className="field-row">
              <label className="field-label">{ui.label} <span className="field-hint">(obrigatório)</span></label>
              <textarea className="field-input" rows={2}
                value={form.text_value}
                onChange={e => setF('text_value', e.target.value)}
                placeholder={`${ui.label}…`} />
            </div>
          )}

          {/* Operation instructions */}
          <div className="field-row">
            <label className="field-label">{t('areas.operation_instructions', 'Instruções da Operação')} <span className="field-hint">(opcional)</span></label>
            <PasteTextarea
              value={form.operation_instructions}
              onChange={v => setF('operation_instructions', v)}
              onImagePaste={addImage}
              placeholder={t('areas.operation_instructions_placeholder', 'Instruções da operação… (pode colar imagens)')}
              rows={3}
            />
          </div>

          {/* Completion notes */}
          <div className="field-row">
            <label className="field-label">{t('areas.completion_notes', 'Notas de Conclusão')} <span className="field-hint">(opcional)</span></label>
            <PasteTextarea
              value={form.completion_notes}
              onChange={v => setF('completion_notes', v)}
              onImagePaste={addImage}
              placeholder={t('areas.completion_notes_placeholder', 'Notas de conclusão… (pode colar imagens)')}
              rows={3}
            />
          </div>

          {/* Attachments — matches LineOperationDetailsModal pattern */}
          <div className="field-row">
            <label className="field-label"><FiPaperclip size={12} /> Anexos <span className="field-hint">(opcional)</span></label>
            {task.line_operations_id ? (
              <FileUpload
                entityType="line_operation"
                entityId={task.line_operations_id}
                compact={true}
                inline={false}
                label=""
                pendingFiles={form.pendingImages || []}
                onPendingFilesChange={imgs =>
                  setForm(prev => ({ ...prev, pendingImages: imgs }))
                }
              />
            ) : (
              <>
                <ImageThumbs files={form.pendingImages || []} onRemove={removeImage} />
                <label className="file-drop">
                  <FiPaperclip size={14} />
                  Anexar ficheiros (guardados ao clicar em Guardar)
                  <input type="file" multiple style={{ display:'none' }}
                    onChange={e => {
                      Array.from(e.target.files || []).forEach(addImage);
                      e.target.value = '';
                    }}
                  />
                </label>
              </>
            )}
          </div>

          {/* Apply to other lines of the same order that have this operation */}
          {siblings.length > 0 && (
            <div className="field-row mtem-apply">
              <button type="button" className="mtem-apply-head"
                onClick={() => setApplyOpen(o => !o)}>
                <FiCopy size={14} />
                <span>Aplicar também a outras linhas</span>
                <span className="mtem-apply-count">
                  {selected.size > 0 ? `${selected.size} de ${siblings.length}` : siblings.length}
                </span>
                <span className="mtem-apply-chev">{applyOpen ? '▾' : '▸'}</span>
              </button>

              {applyOpen && (
                <div className="mtem-apply-body">
                  <div className="mtem-apply-fields">
                    <span className="mtem-apply-fields-label">Campos a copiar:</span>
                    {[
                      ['status', 'Estado'],
                      ['real_dates', 'Datas reais'],
                      ['result', 'Resultado'],
                      ['operation_instructions', 'Instruções'],
                      ['completion_notes', 'Notas'],
                      ['attachments', 'Novos anexos'],
                    ].map(([k, lbl]) => (
                      <label key={k} className="mtem-apply-chk">
                        <input type="checkbox" checked={applyFields[k]}
                          onChange={() => toggleField(k)} />
                        {lbl}
                      </label>
                    ))}
                  </div>

                  <label className="mtem-apply-row mtem-apply-row--all">
                    <input type="checkbox" checked={allSelected} onChange={toggleAll} />
                    <strong>Selecionar todas</strong>
                  </label>

                  <div className="mtem-apply-list">
                    {siblings.map(s => {
                      const meta = TASK_STATUS_META[s.status];
                      return (
                        <label key={s.public_id} className="mtem-apply-row">
                          <input type="checkbox"
                            checked={selected.has(s.public_id)}
                            onChange={() => toggleSibling(s.public_id)} />
                          <span className="mtem-pill mtem-pill--line">L{s.matrix_line_no}</span>
                          <span className="mtem-apply-prod">
                            {s.entered_product_code || s.erp_product_code || '—'}
                            {s.entered_product_color && (
                              <span className="mtem-apply-color"> · {s.entered_product_color}</span>
                            )}
                          </span>
                          {meta && (
                            <span className="mtem-apply-status"
                              style={{ border: '1px solid', ...statusPillStyle(meta, true) }}>
                              {meta.label}
                            </span>
                          )}
                        </label>
                      );
                    })}
                  </div>

                  <div className="field-hint">
                    Campos vazios não substituem os valores já existentes nas outras linhas.
                    Datas planeadas e responsável não são copiados.
                  </div>
                </div>
              )}
            </div>
          )}

          <div className="modal-footer">
            <button type="button" className="btn-secondary" onClick={onCancel} disabled={saving}>
              Cancelar
            </button>
            <button type="submit" className="btn-primary" disabled={saving}>
              {saving ? 'A guardar…' : selected.size > 0 ? `Guardar (+${selected.size})` : 'Guardar'}
            </button>
          </div>
        </form>
      </div>

      {task.line_operations_id && (
        <AuditLogPanel
          isOpen={showAuditLog}
          onClose={() => setShowAuditLog(false)}
          entityType="line_operation"
          entityId={task.line_operations_id?.toString()}
          title={`Histórico de Alterações - ${task.operation_label}`}
        />
      )}
    </div>
  );
};

export default MyTasksEditModal;